import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';

const KeyboardShortcuts = ({ onQuickAdd }) => {
    const [isOpen, setIsOpen] = useState(false);
    const navigate = useNavigate();
    const { isDark } = useTheme();

    useEffect(() => {
        const handleKeyDown = (e) => {
            // Disable shortcuts if typing in an input, textarea, contentEditable element,
            // or if a modifier key is pressed (like Ctrl+C for copy)
            if (
                e.target.tagName === 'INPUT' ||
                e.target.tagName === 'TEXTAREA' ||
                e.target.isContentEditable ||
                e.ctrlKey ||
                e.metaKey ||
                e.altKey
            ) {
                return;
            }

            switch (e.key.toLowerCase()) {
                case '/':
                    e.preventDefault();
                    document.getElementById('global-search-input')?.focus();
                    break;
                case 'n':
                    e.preventDefault();
                    onQuickAdd();
                    break;
                case 'd':
                    e.preventDefault();
                    navigate('/dashboard');
                    break;
                case 'l':
                    e.preventDefault();
                    navigate('/leads');
                    break;
                case 't':
                    e.preventDefault();
                    navigate('/templates');
                    break;
                case 'c':
                    e.preventDefault();
                    navigate('/channels');
                    break;
                case '?':
                    e.preventDefault();
                    setIsOpen(true);
                    break;
                case 'escape':
                    if (isOpen) setIsOpen(false);
                    break;
                default:
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [navigate, onQuickAdd, isOpen]);

    if (!isOpen) return null;

    const shortcuts = [
        { key: '/', label: 'Focus Search' },
        { key: 'n', label: 'New Lead (Quick Add)' },
        { key: 'd', label: 'Dashboard' },
        { key: 'l', label: 'Leads List' },
        { key: 't', label: 'Templates' },
        { key: 'c', label: 'Channels' },
        { key: '?', label: 'Show Help' },
    ];

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                onClick={() => setIsOpen(false)}
            />

            <div className={`relative w-full max-w-md rounded-2xl shadow-2xl overflow-hidden border ${isDark ? 'bg-navy border-white/10' : 'bg-white border-teal/10'
                }`}>
                <div className={`p-6 border-b flex justify-between items-center ${isDark ? 'border-white/10 bg-white/5' : 'border-teal/10 bg-teal/5'
                    }`}>
                    <div className="flex items-center gap-3">
                        <span className={`material-symbols-outlined ${isDark ? 'text-cyan' : 'text-primary'}`}>keyboard</span>
                        <h2 className={`text-xl font-bold ${isDark ? 'text-light' : 'text-darkTeal'}`}>Keyboard Shortcuts</h2>
                    </div>
                    <button
                        onClick={() => setIsOpen(false)}
                        className={`p-2 rounded-full transition-colors ${isDark ? 'hover:bg-white/10 text-light/50' : 'hover:bg-teal/10 text-darkTeal/50'
                            }`}
                    >
                        <span className="material-symbols-outlined">close</span>
                    </button>
                </div>

                <div className="p-6">
                    <div className="grid grid-cols-1 gap-4">
                        {shortcuts.map((shortcut) => (
                            <div key={shortcut.key} className="flex items-center justify-between">
                                <span className={isDark ? 'text-light/70' : 'text-darkTeal/70'}>
                                    {shortcut.label}
                                </span>
                                <kbd className={`min-w-[32px] h-8 flex items-center justify-center px-2 rounded-lg font-mono text-sm font-bold shadow-sm border ${isDark
                                    ? 'bg-navy-light border-white/20 text-cyan shadow-black/20'
                                    : 'bg-slate-100 border-teal/20 text-primary shadow-teal/10'
                                    }`}>
                                    {shortcut.key}
                                </kbd>
                            </div>
                        ))}
                    </div>

                    <div className={`mt-8 p-4 rounded-xl text-center text-xs font-medium ${isDark ? 'bg-white/5 text-light/40' : 'bg-teal/5 text-darkTeal/40'
                        }`}>
                        Press <span className="font-bold text-primary dark:text-cyan">ESC</span> to close this modal
                    </div>
                </div>
            </div>
        </div>
    );
};

export default KeyboardShortcuts;
